// src/components/admin/bespokeai/CommunicationsAI.jsx
import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../../../utils/firebase';
import { fetchCommunicationsContext } from '../../../api/aiContextApi';
import DailyTokenCounter from './DailyTokenCounter';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const PRESET_PROMPTS = [
  'Write a welcome email for new members',
  'Draft a 60-day re-engagement message',
  'Create a WhatsApp message for an upcoming event',
  'Write a volunteer appreciation message',
  'Draft a push notification for a new shift',
];

const INITIAL_MESSAGE = {
  id: 'init',
  role: 'assistant',
  type: 'ai',
  content: "Hello! I'm BespokeAI, your intelligent assistant for communications and campaigns. I can help you craft email campaigns, WhatsApp messages, and push notifications tailored to your members and volunteers. How can I help you today?",
};

export default function CommunicationsAI({ orgId, adminId, onUseInEmailEditor, onUseInWhatsAppEditor, onUseInPushEditor }) {
  const [messages,  setMessages]  = useState([INITIAL_MESSAGE]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const counterRef = useRef(null);
  const chatRef   = useRef(null);
  const inputRef  = useRef(null);
  const prevLenRef = useRef(1);

  useEffect(() => {
    const prev = prevLenRef.current;
    prevLenRef.current = messages.length;
    if (messages.length > prev && prev > 0 && chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading || limitReached) return;

    const updatedMessages = [...messages, { id: Date.now(), role: 'user', type: 'user', content: trimmed }];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const [token, context] = await Promise.all([
        auth.currentUser.getIdToken(),
        fetchCommunicationsContext(orgId),
      ]);

      const apiMessages = updatedMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messages: apiMessages, module: 'communications', context }),
      });
      const data = await res.json();
      if (data.dailyUsage) counterRef.current?.update(data.dailyUsage);
      const reply = data.success ? data.text : (data.message || 'Something went wrong. Please try again.');

      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'ai', content: reply, showActions: true }]);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'ai', content: `Error: ${err.message}`, showActions: false }]);
    }
    setLoading(false);
  };

  const handleSend = () => sendMessage(input);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleUseIn = (channel, content) => {
    if (channel === 'email'    && onUseInEmailEditor)    onUseInEmailEditor(content);
    if (channel === 'whatsapp' && onUseInWhatsAppEditor) onUseInWhatsAppEditor(content);
    if (channel === 'push'     && onUseInPushEditor)     onUseInPushEditor(content);
  };

  return (
    <div className="bb-ai-container">
      <div className="bb-ai-header">
        <h3>BespokeAI — Communications</h3>
        <p>AI-powered copy for emails, WhatsApp messages, and push notifications</p>
      </div>

      <DailyTokenCounter ref={counterRef} orgId={orgId} onLimitReached={setLimitReached} />

      <div className="bb-ai-messages" ref={chatRef}>
        {messages.map(msg => (
          <div key={msg.id} className={`bb-ai-msg bb-ai-msg-${msg.type}`}>
            <div className={`bb-ai-bubble-${msg.type === 'user' ? 'user' : 'ai'}`}>
              <pre>{msg.content}</pre>
            </div>
            {msg.type === 'ai' && msg.showActions && (
              <div className="bb-ai-actions">
                <button className="bb-ai-action-btn" onClick={() => handleUseIn('email', msg.content)}>
                  Use in Email Editor
                </button>
                <button className="bb-ai-action-btn" onClick={() => handleUseIn('whatsapp', msg.content)}>
                  Use in WhatsApp Editor
                </button>
                <button className="bb-ai-action-btn" onClick={() => handleUseIn('push', msg.content)}>
                  Use in Push Editor
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="bb-ai-msg bb-ai-msg-ai">
            <div className="bb-ai-bubble-ai" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ animation: 'pulse 1s infinite', opacity: 0.6 }}>●</span>
              <span style={{ animation: 'pulse 1s infinite 0.2s', opacity: 0.6 }}>●</span>
              <span style={{ animation: 'pulse 1s infinite 0.4s', opacity: 0.6 }}>●</span>
            </div>
          </div>
        )}
      </div>

      <div className="bb-ai-presets">
        {PRESET_PROMPTS.map((p, i) => (
          <button key={i} className="bb-ai-preset-chip" onClick={() => sendMessage(p)} disabled={loading || limitReached}>
            {p}
          </button>
        ))}
      </div>

      <div className="bb-ai-input-row">
        <textarea
          ref={inputRef}
          className="bb-ai-input"
          placeholder={limitReached ? 'Daily AI usage limit reached. Resets at 00:00:01.' : "Ask BespokeAI to write copy for your campaign..."}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={limitReached}
          rows={1}
        />
        <button className="bb-ai-send-btn" onClick={handleSend} disabled={loading || !input.trim() || limitReached}>
          Send
        </button>
      </div>
    </div>
  );
}
